import { Link } from 'react-router';
import { StatusBadge } from '../../components/Badge';
import { ErrorNotice, LoadingNotice } from '../../components/ErrorNotice';
import { AccessDenied } from '../../components/AccessDenied';
import { formatDate, formatPercent, humanize } from '../../lib/format';
import { phrase, useI18n } from '../../lib/i18n/runtime';
import { ApprovalSource } from '../equity/EquityPositionDetails';
import { can, useMembership } from '../workspace/membership-context';
import { useFounders } from './founders.api';

export function FoundersPage() {
  const { locale, t } = useI18n();
  const membership = useMembership();
  const allowed = can(membership, 'founders.read');
  const founders = useFounders(membership.workspace.id, allowed);

  if (!allowed) {
    return <AccessDenied />;
  }
  if (founders.isPending) {
    return <LoadingNotice>{t('loadingFounders')}</LoadingNotice>;
  }
  if (founders.isError) {
    return <ErrorNotice error={founders.error} title={t('foundersError')} />;
  }
  const { items, equityVisible, asOf } = founders.data;
  const base = `/w/${membership.workspace.id}/founders`;

  return (
    <div className="stack">
      <h1>{t('navFounders')}</h1>
      {equityVisible && (
        <p className="meta">
          {locale === 'HY'
            ? `Բաժնեմասը՝ ${formatDate(asOf)} (հաստատված կապիտալի աղյուսակ)։`
            : `Equity as of ${formatDate(asOf)} (approved cap table).`}
        </p>
      )}
      {items.length === 0 ? (
        <p className="meta">{t('noFounders')}</p>
      ) : (
        <div className="table-scroll" role="region" aria-label={t('navFounders')} tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('name')}</th>
                <th scope="col">{t('roleAndTitle')}</th>
                <th scope="col">{t('status')}</th>
                <th scope="col">{t('founderSince')}</th>
                {equityVisible && (
                  <>
                    <th scope="col">{t('ownership')}</th>
                    <th scope="col">{t('effectiveFrom')}</th>
                    <th scope="col">{t('source')}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((founder) => (
                <tr key={founder.id}>
                  <th scope="row">
                    <Link to={`${base}/${founder.id}`}>{founder.displayName}</Link>
                    {founder.legalName !== null && founder.legalName !== founder.displayName && (
                      <span className="meta"> ({founder.legalName})</span>
                    )}
                  </th>
                  <td>
                    {humanize(founder.role)}
                    {founder.title !== null ? ` · ${founder.title}` : ''}
                  </td>
                  <td>
                    <StatusBadge status={founder.status} />
                  </td>
                  <td>{founder.founderSince !== null ? formatDate(founder.founderSince) : '—'}</td>
                  {equityVisible && (
                    <>
                      <td className="numeric">
                        {founder.currentEquity !== null
                          ? formatPercent(founder.currentEquity.percentage)
                          : phrase('None')}
                      </td>
                      <td>
                        {founder.currentEquity !== null
                          ? formatDate(founder.currentEquity.effectiveFrom)
                          : '—'}
                      </td>
                      <td>
                        {founder.currentEquity !== null ? (
                          <>
                            Version #{founder.currentEquity.versionNumber} ·{' '}
                            <ApprovalSource
                              approvalDecisionId={founder.currentEquity.approvalDecisionId}
                              approvalReference={founder.currentEquity.approvalReference}
                            />
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
